import axios from "axios";
import { ERRORS } from "../constants/errorMessages";
import { isTruthy } from "./fp";
/**
 * Checks if error is a timeout (multiple ways this can happen)
 */
const isTimeoutError = (error) => {
    if (!axios.isAxiosError(error))
        return false;
    // Check 1: Axios timeout (when using timeout config)
    if (error.code === "ECONNABORTED")
        return true;
    // Check 2: Socket timeout
    if (error.code === "ETIMEDOUT")
        return true;
    // Check 3: HTTP 408 Request Timeout
    if (error.response?.status === 408)
        return true;
    // Check 4: HTTP 504 Gateway Timeout
    if (error.response?.status === 504)
        return true;
    return false;
};
/**
 * Checks if error is network-related (no response received)
 */
const isNetworkError = (error) => {
    if (!axios.isAxiosError(error))
        return false;
    // No response object means network failure
    if (!error.response) {
        return (error.code === "ERR_NETWORK" ||
            error.code === "ECONNREFUSED" ||
            error.code === "ENOTFOUND" ||
            error.message?.includes("Network Error"));
    }
    return false;
};
/**
 * Checks if error is authentication-related
 */
const isAuthError = (error) => {
    if (!axios.isAxiosError(error))
        return false;
    return error.response?.status === 401 || error.response?.status === 403;
};
/**
 * Checks if error is server unavailable
 */
const isServerUnavailable = (error) => {
    if (!axios.isAxiosError(error))
        return false;
    const status = error.response?.status;
    return status === 502 || status === 503;
};
/**
 * @param e: the error to check.
 * @returns: true if the error contains a data object, marked true for visible_error, with a non-empty array of errors. Undefined if AxiosError response and response.data are not both defined objects. Else, false.
 */
export const isErrorResponseValid = (e) => {
    return !!(e.response &&
        e.response.data &&
        typeof e.response.data === "object" &&
        e.response.data.visible_error &&
        Array.isArray(e.response.data.errors) &&
        e.response.data.errors.length > 0);
};
// TODO: Should we create an error util file instead?
/**
 * Error return data type can be any here, because we don't care about it, but we're forced to define it
 * @param e: the error to check.
 * @return: array of string of errors from backend, usually user-friendly
 */
export const deriveErrorResponse = (e) => {
    return e?.response?.data?.errors && Array.isArray(e.response.data.errors)
        ? e.response.data.errors
        : [];
};
/**
 * @param apiError: the error to derive details from.
 * @param fallbackMessage: message to use if no detail error from api was found
 * @return: string message with error details
 */
export const getDetailedErrorMessage = (apiError, fallbackMessage) => {
    const apiErrorMessages = deriveErrorResponse(apiError);
    return apiErrorMessages.length > 0
        ? `Details: ${apiErrorMessages.filter(isTruthy).join(",")}`
        : fallbackMessage;
};
/**
 * Enhanced version of getDetailedErrorMessage with priority-based error classification.
 * Used exclusively by MadSDKAPIError base class. Other modules continue to use
 * getDetailedErrorMessage to avoid changing existing behavior.
 *
 * Checks conditions in priority order (most specific to least specific).
 * First match wins, so order matters.
 *
 * @param apiError: the error to derive details from.
 * @param fallbackMessage: message to use if no detail error from api was found
 * @return: string message with error details
 */
export const getEnhancedErrorMessage = (apiError, fallbackMessage) => {
    // Priority 1: Backend explicitly provided error messages
    const apiErrorMessages = deriveErrorResponse(apiError).filter((msg) => typeof msg === "string" && msg.trim().length > 0);
    if (apiErrorMessages.length > 0) {
        return ERRORS.GENERAL.BACKEND_DETAILS(apiErrorMessages);
    }
    // Priority 2: Timeout errors (before we check response status)
    if (isTimeoutError(apiError)) {
        const timeoutValue = apiError.config?.timeout;
        if (timeoutValue) {
            return ERRORS.TIMEOUT.WITH_DURATION(timeoutValue / 1000);
        }
        return ERRORS.TIMEOUT.GENERIC;
    }
    // Priority 3: Network errors (no response received)
    if (isNetworkError(apiError)) {
        if (apiError.code === "ENOTFOUND") {
            return ERRORS.NETWORK.DNS_FAILED;
        }
        if (apiError.code === "ECONNREFUSED") {
            return ERRORS.NETWORK.CONNECTION_REFUSED;
        }
        return ERRORS.NETWORK.GENERIC;
    }
    // Priority 4: Authentication errors
    if (isAuthError(apiError)) {
        const status = apiError.response?.status;
        if (status === 401) {
            return ERRORS.AUTH.SESSION_EXPIRED;
        }
        if (status === 403) {
            return ERRORS.AUTH.ACCESS_DENIED;
        }
    }
    // Priority 5: Server unavailable
    if (isServerUnavailable(apiError)) {
        const status = apiError.response?.status;
        if (status === 502) {
            return ERRORS.SERVER.BAD_GATEWAY;
        }
        if (status === 503) {
            return ERRORS.SERVER.SERVICE_UNAVAILABLE;
        }
    }
    // Priority 6: HTTP status codes we can explain
    if (axios.isAxiosError(apiError) && apiError.response?.status) {
        const status = apiError.response.status;
        if (status === 400) {
            return ERRORS.HTTP.BAD_REQUEST;
        }
        if (status === 404) {
            return ERRORS.HTTP.NOT_FOUND;
        }
        if (status === 409) {
            return ERRORS.HTTP.CONFLICT;
        }
        if (status === 429) {
            return ERRORS.HTTP.RATE_LIMIT;
        }
        if (status >= 500) {
            return ERRORS.HTTP.SERVER_ERROR(status);
        }
    }
    // Priority 7: Fallback
    return fallbackMessage;
};
//# sourceMappingURL=validation.js.map